import React, { useState } from 'react';

import { Grid, Chip } from '@material-ui/core';
import { StyledAvatar } from 'components/avatars';
import TextParseComponent from 'components/feed/TextParseComponent';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import SidepanelFooter from 'screens/admin/common/sidepanelFooter';
import SidepanelHeader from 'screens/admin/common/sidepanelHeader';
import styled from 'styled-components';
import {
  StyledTypography,
  StyledLink,
  GutterCSS,
  VerticallyCenterContainer,
  RelativePositionFullHeightContainer,
} from 'styles/common';
import { checkGoalModuleEnabled } from 'utils/helpers/moduleAccess';

import {
  FEEDBACK_FORM_VISIBILITY,
  CONTINUOUS_FEEDBACK_FORM_STATUS,
  CONTINUOUS_FEEDBACK_RELOAD_TYPE,
  SHARE_FEEDBACK_TYPES,
  ALERT_MESSAGES,
} from 'constants/ProjectConstants';
import { getProfileRoute } from 'constants/helper';
import { StyledIconV2 } from 'constants/icons';
import theme from 'constants/theme';

import { archiveFeedback } from 'services/continuousFeedback';
import { showErrorSnackbar } from 'services/snackbar';

import DateComp from 'uiToolkit/DateComp';
import { Typography13 } from 'uiToolkit/Typography';

import { FeedbackIconPack } from './common/SelectFeedbackType';
import { VisibilityIconPack } from './common/SelectVisibility';

const SectionContainer = styled.div`
  margin: 0.5rem 0 2rem 0;
  ${GutterCSS}
`;

const StyledBody = styled.div`
  padding: 0 1.5rem 3rem 1.5rem;
  height: 80vh;
  width: 100%;
  overflow: auto;
`;

const AvatarContainer = styled.div`
  order: 0;
  margin-right: 0.5rem;
`;

const DateContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  max-width: 70px;
`;

const StyledGrid = styled(Grid)`
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

const StyledChip = styled(Chip)`
  background-color: #f7f7f7;
  padding: 4px 8px;
  .MuiChip-avatar {
    width: 18px;
    height: 18px;
  }
  .MuiChip-label {
    font-size: 14px;
  }
`;

const ViewRequestedFeedback = ({ handleClose, requestFeedbackDetails = {}, loggedInEmployee, handleResetData }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const { review, employee } = requestFeedbackDetails;

  const [selectedFormVisibility] = useState(FEEDBACK_FORM_VISIBILITY[0]);

  const handleArchiveFeedback = () => {
    setLoading(true);

    archiveFeedback({ feedbackId: review.uuid })
      .then(() => {
        handleResetData({ reloadType: CONTINUOUS_FEEDBACK_RELOAD_TYPE.ARCHIVED });
        handleClose();
      })
      .catch(() => dispatch(showErrorSnackbar(ALERT_MESSAGES.CONTINUOUS_FEEDBACK.ARCHIVE.FAILURE)))
      .finally(() => {
        setLoading(false);
      });
  };

  const redirectToProfile = () => {
    const route = getProfileRoute(employee.id);
    history.push(route);
    return {};
  };

  const getPrimaryActionStatus = () => false;

  return (
    <RelativePositionFullHeightContainer>
      <SidepanelHeader
        icon={
          <AvatarContainer>
            <StyledAvatar size="fixed32px" src={employee.profilePhotoSrc} />
          </AvatarContainer>
        }
        text={
          <span>
            Feedback {loggedInEmployee?.id === review?.receiverEmployeeId?.toString() ? 'From ' : 'For '}
            <StyledLink display="inline" href="#" onClick={redirectToProfile}>
              @{employee.displayName}
            </StyledLink>
          </span>
        }
        onCancel={handleClose}
      />
      <StyledBody>
        <SectionContainer style={{ marginTop: '1.5rem' }}>
          <Grid container spacing={8} justify="space-between">
            <StyledGrid item sm={6}>
              <Typography13 color={theme.palette.grey.dark} variant="body2" bottomGutter={1}>
                Requested by
              </Typography13>
              <div>
                <StyledChip
                  avatar={<StyledAvatar size="small" src={review?.requesterEmployee?.profilePhoto} />}
                  label={
                    (review?.requesterEmployee?.displayName ?? '') +
                    (review?.requesterEmployee?.designation ? `(${review?.requesterEmployee?.designation})` : '')
                  }
                />
              </div>
            </StyledGrid>
            {review?.submissionStatus === CONTINUOUS_FEEDBACK_FORM_STATUS.COMPLETED ? (
              <StyledGrid item sm={6}>
                <Typography13 color={theme.palette.grey.dark} variant="body2" bottomGutter={1}>
                  Feedback Date
                </Typography13>
                <DateContainer>
                  {review.submissionDateFormatted && (
                    <DateComp
                      tooltip="Submitted on"
                      editable={false}
                      startDateTime={review.submissionDateFormatted.timeStamp}
                      color={review.submissionDateFormatted.color}
                      isCrossed={review.submissionDateFormatted.isCrossed}
                    />
                  )}
                </DateContainer>
              </StyledGrid>
            ) : null}
          </Grid>
        </SectionContainer>
        <SectionContainer>
          <Grid container spacing={8} justify="space-between">
            {review?.feedbackPost?.macrosRes?.attributes?.sentiment && (
              <Grid item sm={6}>
                <Grid container spacing={8} justify="space-between">
                  <Grid item sm={5}>
                    <Typography13 color={theme.palette.grey.dark} variant="body2" bottomGutter={1}>
                      Feedback type
                    </Typography13>
                    <VerticallyCenterContainer>
                      {SHARE_FEEDBACK_TYPES.find(
                        (item) => item.value === review?.feedbackPost?.macrosRes?.attributes?.sentiment
                      ) && (
                        <StyledIconV2 size="fixed14px" color="textSecondary">
                          {
                            FeedbackIconPack[
                              SHARE_FEEDBACK_TYPES.find(
                                (item) => item.value === review?.feedbackPost?.macrosRes?.attributes?.sentiment
                              )?.icon
                            ]
                          }
                        </StyledIconV2>
                      )}
                      <Typography13 leftGutter={1}>
                        {SHARE_FEEDBACK_TYPES.find(
                          (item) => item.value === review?.feedbackPost?.macrosRes?.attributes?.sentiment
                        )?.label || 'NA'}
                      </Typography13>
                    </VerticallyCenterContainer>
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid item sm={6}>
              <Typography13 color={theme.palette.grey.dark} variant="body2" bottomGutter={1}>
                Feedback visibility
              </Typography13>
              <VerticallyCenterContainer>
                {selectedFormVisibility?.icon && (
                  <StyledIconV2 size="fixed14px" color="textSecondary">
                    {VisibilityIconPack[selectedFormVisibility?.icon]}
                  </StyledIconV2>
                )}
                <Typography13 leftGutter={1}>
                  {FEEDBACK_FORM_VISIBILITY.find((item) => item.value === review.visibility).label}
                </Typography13>
              </VerticallyCenterContainer>
            </Grid>
          </Grid>
        </SectionContainer>

        {checkGoalModuleEnabled() && (
          <SectionContainer topGutter={1}>
            {review && review.goal ? (
              <>
                <Typography13 color={theme.palette.grey.dark} variant="body2" bottomGutter={1}>
                  Requested on
                </Typography13>
                <TextParseComponent text={review.goal.text} res={review.goal.macros} />
              </>
            ) : null}
          </SectionContainer>
        )}

        <SectionContainer topGutter={1}>
          <Typography13 color={theme.palette.grey.dark} variant="body2" bottomGutter={1}>
            Request details or question
          </Typography13>
          <StyledTypography variant="body2" bottomGutter={1}>
            {review?.questionText}
          </StyledTypography>
        </SectionContainer>

        <SectionContainer bottomGutter={2}>
          {review?.submissionStatus === CONTINUOUS_FEEDBACK_FORM_STATUS.COMPLETED ? (
            <TextParseComponent text={review.feedbackPost.text} res={review.feedbackPost.macrosRes} />
          ) : null}
        </SectionContainer>
      </StyledBody>
      <SidepanelFooter
        onSave={handleArchiveFeedback}
        onCancel={handleClose}
        loading={loading}
        disabled={!!review?.isArchived}
        primaryActionText={review?.isArchived ? 'Archived' : 'Archive'}
        showPrimaryAction={getPrimaryActionStatus()}
        secondaryActionText="Close"
      />
    </RelativePositionFullHeightContainer>
  );
};

export default ViewRequestedFeedback;
